import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  IconButton,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
  Tooltip,
  CircularProgress,
  Alert,
  Badge,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  Zoom,
  Slide,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today,
  CalendarMonth,
  Event,
  Image as ImageIcon,
  Facebook,
  Instagram,
  LinkedIn,
  Twitter,
  YouTube,
  Visibility,
  Edit,
  Delete,
  Schedule,
  AccessTime,
  TrendingUp,
  Add as AddIcon,
} from '@mui/icons-material';
import { fetchMonthlyPosts } from '../../Action/api';
import { cancelScheduledPost } from '../../Action/runLaterApi';
import { getAuthTokenPayload } from '../../Utils/index';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { alpha, keyframes } from '@mui/material/styles';

dayjs.extend(utc);
dayjs.extend(timezone);

// Vibrant gradient color scheme for platforms
const PLATFORM_COLORS = {
  Instagram: {
    primary: '#E1306C',
    gradient: 'linear-gradient(135deg, #833AB4 0%, #FD1D1D 50%, #FCAF45 100%)',
    light: '#FDE8F0',
    glow: 'rgba(225, 48, 108, 0.3)',
  },
  Facebook: {
    primary: '#1877F2',
    gradient: 'linear-gradient(135deg, #4267B2 0%, #1877F2 100%)',
    light: '#E7F3FF',
    glow: 'rgba(24, 119, 242, 0.3)',
  },
  LinkedIn: {
    primary: '#0A66C2',
    gradient: 'linear-gradient(135deg, #0A66C2 0%, #0077B5 100%)',
    light: '#E0F2FE',
    glow: 'rgba(10, 102, 194, 0.3)',
  },
  Twitter: {
    primary: '#1DA1F2',
    gradient: 'linear-gradient(135deg, #1DA1F2 0%, #0891D1 100%)',
    light: '#E0F2FE',
    glow: 'rgba(29, 161, 242, 0.3)',
  },
  YouTube: {
    primary: '#FF0000',
    gradient: 'linear-gradient(135deg, #FF0000 0%, #CC0000 100%)',
    light: '#FEE2E2',
    glow: 'rgba(255, 0, 0, 0.3)',
  },
  Default: {
    primary: '#6B7280',
    gradient: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
    light: '#F3F4F6',
    glow: 'rgba(107, 114, 128, 0.3)',
  },
};

const PLATFORM_ICONS = {
  Instagram: Instagram,
  Facebook: Facebook,
  LinkedIn: LinkedIn,
  Twitter: Twitter,
  YouTube: YouTube,
};

// Animations
const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
`;

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const CalendarSchedule = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [cancellingPost, setCancellingPost] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [openDayPosts, setOpenDayPosts] = useState(false);
  const [selectedDayPosts, setSelectedDayPosts] = useState([]);
  const [selectedDayDate, setSelectedDayDate] = useState(null);
  
  // Schedule dialog state
  const [openSchedule, setOpenSchedule] = useState(false);
  const [selectedScheduleDate, setSelectedScheduleDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('09:00');

  // Get client ID from JWT token
  const clientId = getAuthTokenPayload()?.clientId;

  const todayUtc = dayjs.utc().startOf('day');
  const postedCount = posts.filter((post) => {
    if (!post?.scheduleDateTime) return false;
    return dayjs.utc(post.scheduleDateTime).startOf('day').isBefore(todayUtc, 'day');
  }).length;
  const upcomingCount = posts.filter((post) => {
    if (!post?.scheduleDateTime) return false;
    return dayjs.utc(post.scheduleDateTime).startOf('day').isSame(todayUtc, 'day') ||
      dayjs.utc(post.scheduleDateTime).startOf('day').isAfter(todayUtc, 'day');
  }).length;
  const totalCount = posts.length;

  // Fetch posts for current month
  useEffect(() => {
    if (clientId) {
      fetchPosts();
    }
  }, [currentMonth, clientId]);

  const fetchPosts = async () => {
    setLoading(true);
    setError(null);
    try {
      const monthYear = currentMonth.format('MMM-YYYY');
      const response = await fetchMonthlyPosts(monthYear, clientId);
      
      if (response.success) {
        let postsArray = [];
        
        if (Array.isArray(response.data)) {
          postsArray = response.data;
        } else if (response.data?.posts && Array.isArray(response.data.posts)) {
          postsArray = response.data.posts;
        } else {
          postsArray = [];
        }
        
        setPosts(postsArray);
      } else {
        setError(response.message || 'Failed to fetch posts');
      }
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Failed to load scheduled posts');
    } finally {
      setLoading(false);
    }
  };

  // Get days in month
  const getDaysInMonth = () => {
    const startOfMonth = currentMonth.startOf('month');
    const endOfMonth = currentMonth.endOf('month');
    const daysInMonth = endOfMonth.date();
    const startDay = startOfMonth.day();

    const days = [];
    
    // Add empty slots for days before month starts
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }
    
    // Add all days in month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(currentMonth.date(i));
    }
    
    return days;
  };

  // Get posts for a specific date
  const getPostsForDate = (date) => {
    if (!date) return [];
    
    return posts.filter(post => {
      if (!post.scheduleDateTime) return false;
      
      // Parse schedule date and normalize to start of day in UTC
      const scheduleDate = dayjs.utc(post.scheduleDateTime).startOf('day');
      // Parse calendar date and normalize to start of day in UTC
      const calendarDate = dayjs(date).utc().startOf('day');
      
      return scheduleDate.isSame(calendarDate, 'day');
    });
  };

  // Navigate months
  const handlePreviousMonth = () => {
    setCurrentMonth(currentMonth.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentMonth(currentMonth.add(1, 'month'));
  };

  const handleToday = () => {
    setCurrentMonth(dayjs());
  };

  // Handle post preview
  const handlePostClick = (post) => {
    setSelectedPost(post);
    setOpenPreview(true);
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
    setSelectedPost(null);
    setCancelError(null);
    setCancelSuccess(false);
  };

  const handleOpenDayPosts = (date, dayPosts) => {
    setSelectedDayDate(date);
    setSelectedDayPosts(dayPosts);
    setOpenDayPosts(true);
  };

  const handleCloseDayPosts = () => {
    setOpenDayPosts(false);
    setSelectedDayPosts([]);
    setSelectedDayDate(null);
  };

  // Handle cancel post
  const handleCancelPost = async () => {
    if (!selectedPost || !selectedPost.scheduleDateTime) {
      setCancelError('Invalid post data');
      return;
    }

    // Check if post is in the future
    const postDate = dayjs(selectedPost.scheduleDateTime);
    if (postDate.isBefore(dayjs())) {
      setCancelError('Cannot cancel past posts');
      return;
    }

    setCancellingPost(true);
    setCancelError(null);
    setCancelSuccess(false);

    try {
      const response = await cancelScheduledPost(clientId, selectedPost.scheduleDateTime);
      
      if (response.success || response.status !== false) {
        setCancelSuccess(true);
        // Refresh posts after successful cancellation
        setTimeout(() => {
          fetchPosts();
          handleClosePreview();
        }, 1500);
      } else {
        setCancelError(response.message || 'Failed to cancel post');
      }
    } catch (error) {
      console.error('Error canceling post:', error);
      setCancelError(error.message || 'Failed to cancel post. Please try again.');
    } finally {
      setCancellingPost(false);
    }
  };

  // Handle schedule dialog
  const handleDateClick = (date) => {
    const isFuture = date.isAfter(dayjs(), 'day') || date.isSame(dayjs(), 'day');
    if (isFuture) {
      setSelectedScheduleDate(date);
      setSelectedTime('09:00');
      setOpenSchedule(true);
    }
  };

  const handleCloseSchedule = () => {
    setOpenSchedule(false);
    setSelectedScheduleDate(null);
    setSelectedTime('09:00');
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  const handleSchedulePost = () => {
    // Combine date and time
    const [hours, minutes] = selectedTime.split(':');
    const scheduledDateTime = selectedScheduleDate
      .hour(parseInt(hours))
      .minute(parseInt(minutes))
      .second(0);
    
    // Store in localStorage for safe-post page
    localStorage.setItem('scheduledDateTime', scheduledDateTime.toISOString());
    localStorage.setItem('scheduleDate', selectedScheduleDate.format('YYYY-MM-DD'));
    localStorage.setItem('scheduleTime', selectedTime);
    
    // Navigate to safe-post
    window.location.href = '/safe-post';
  };

  // Time options
  const timeOptions = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
    '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
    '17:00', '17:30', '18:00', '18:30', '19:00', '19:30',
    '20:00', '20:30', '21:00', '21:30', '22:00', '22:30',
  ];

  const quickPickTimes = [
    '08:00', '08:30', '09:00', '09:30', '12:00', '12:30',
    '15:00', '15:30', '18:00', '18:30', '21:00', '21:30',
  ];

  // Render day cell
  const renderDayCell = (date, dayPosts) => {
    if (!date) {
      return (
        <Box
          sx={{
            aspectRatio: isMobile ? '1/1.2' : '1/1',
            bgcolor: 'transparent',
          }}
        />
      );
    }

    const isToday = date.isSame(dayjs(), 'day');
    const isPast = date.isBefore(dayjs(), 'day');
    const hasPosts = dayPosts.length > 0;
    const isWeekend = date.day() === 0 || date.day() === 6;

    // Get dominant platform color if posts exist
    const dominantPlatform = hasPosts ? (dayPosts[0].platforms?.[0] || 'Default') : 'Default';
    const platformColor = PLATFORM_COLORS[dominantPlatform] || PLATFORM_COLORS.Default;

    return (
      <Zoom in timeout={300}>
        <Paper
          elevation={0}
          sx={{
            aspectRatio: { xs: '1/1.35', sm: '1/1.2', md: '1/1' },
            p: { xs: 0.5, sm: 1, md: 1.5 },
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: { xs: 1.5, sm: 2, md: 3 },
            background: isToday
              ? `linear-gradient(135deg, ${alpha('#FF6B6B', 0.15)} 0%, ${alpha('#4ECDC4', 0.15)} 100%)`
              : hasPosts
                ? `linear-gradient(135deg, ${alpha(platformColor.primary, 0.08)} 0%, ${alpha(platformColor.primary, 0.02)} 100%)`
                : isWeekend
                  ? alpha(theme.palette.primary.main, 0.02)
                  : theme.palette.background.paper,
            border: { xs: '1px solid', md: '2px solid' },
            borderColor: isToday
              ? '#FF6B6B'
              : hasPosts
                ? alpha(platformColor.primary, 0.4)
                : alpha(theme.palette.divider, 0.3),
            boxShadow: isToday
              ? isMobile ? `0 4px 12px ${alpha('#FF6B6B', 0.2)}` : `0 8px 24px ${alpha('#FF6B6B', 0.25)}, inset 0 1px 0 ${alpha('#fff', 0.1)}`
              : hasPosts
                ? isMobile ? 'none' : `0 4px 16px ${alpha(platformColor.primary, 0.15)}, inset 0 1px 0 ${alpha('#fff', 0.05)}`
                : 'none',
            opacity: isPast && !hasPosts ? 0.4 : 1,
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&:hover': hasPosts ? {} : (date.isAfter(dayjs(), 'day') || date.isSame(dayjs(), 'day')) ? {
              transform: 'translateY(-4px) scale(1.02)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.2)}`,
              borderColor: theme.palette.primary.main,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
            } : {},
            '&::before': isToday ? {
              content: '""',
              position: 'absolute',
              top: -2,
              left: -2,
              right: -2,
              bottom: -2,
              background: `linear-gradient(135deg, #FF6B6B, #4ECDC4, #45B7D1, #96CEB4)`,
              borderRadius: 3,
              zIndex: -1,
              opacity: 0.6,
              animation: `${pulse} 3s ease-in-out infinite`,
            } : {},
          }}
        >
          {/* Decorative gradient overlay for posts */}
          {hasPosts && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '40%',
                background: `linear-gradient(180deg, ${alpha(platformColor.primary, 0.1)} 0%, transparent 100%)`,
                borderRadius: '12px 12px 0 0',
                pointerEvents: 'none',
              }}
            />
          )}

          {/* Today indicator ribbon */}
          {isToday && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: -32,
                width: 100,
                py: 0.5,
                background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
                transform: 'rotate(-45deg)',
                boxShadow: `0 4px 8px ${alpha('#FF6B6B', 0.3)}`,
                zIndex: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  textAlign: 'center',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Today
              </Typography>
            </Box>
          )}

          {/* Date number with enhanced styling */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, zIndex: 1 }}>
            <Box
              onClick={(e) => {
                e.stopPropagation();
                if (date.isAfter(dayjs(), 'day') || date.isSame(dayjs(), 'day')) {
                  handleDateClick(date);
                }
              }}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: isMobile ? 32 : 40,
                height: isMobile ? 32 : 40,
                borderRadius: 2.5,
                cursor: (date.isAfter(dayjs(), 'day') || date.isSame(dayjs(), 'day')) ? 'pointer' : 'default',
                background: isToday
                  ? 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)'
                  : hasPosts
                    ? platformColor.gradient
                    : `linear-gradient(135deg, ${alpha(theme.palette.text.primary, 0.05)} 0%, ${alpha(theme.palette.text.primary, 0.02)} 100%)`,
                boxShadow: isToday
                  ? `0 4px 12px ${alpha('#FF6B6B', 0.4)}, inset 0 1px 2px ${alpha('#fff', 0.3)}`
                  : hasPosts
                    ? `0 3px 8px ${platformColor.glow}, inset 0 1px 2px ${alpha('#fff', 0.2)}`
                    : 'none',
                border: `2px solid ${isToday ? alpha('#fff', 0.3) : hasPosts ? alpha('#fff', 0.2) : 'transparent'}`,
                transition: 'all 0.3s',
                '&:hover': (date.isAfter(dayjs(), 'day') || date.isSame(dayjs(), 'day')) ? {
                  transform: 'scale(1.1)',
                  boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                } : {},
                '&::after': hasPosts ? {
                  content: '""',
                  position: 'absolute',
                  inset: -3,
                  borderRadius: 3,
                  padding: 2,
                  background: platformColor.gradient,
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                  opacity: 0.5,
                } : {},
              }}
            >
              <Typography
                variant={isMobile ? 'body2' : 'h6'}
                fontWeight={700}
                sx={{
                  color: isToday || hasPosts ? 'white' : theme.palette.text.primary,
                  textShadow: isToday || hasPosts ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                {date.format('D')}
              </Typography>
            </Box>

            {/* Post count badge */}
            {hasPosts && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${alpha('#10b981', 0.9)} 0%, ${alpha('#059669', 0.9)} 100%)`,
                  boxShadow: `0 4px 12px ${alpha('#10b981', 0.4)}`,
                  border: `1px solid ${alpha('#fff', 0.2)}`,
                  zIndex: 3,
                }}
              >
                <Event sx={{ fontSize: isMobile ? '0.85rem' : '1rem', color: 'white' }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'white',
                    fontWeight: 800,
                    fontSize: isMobile ? '0.7rem' : '0.75rem',
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                  }}
                >
                  {dayPosts.length}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Posts preview with enhanced styling */}
          <Box 
            sx={{ 
              flex: 1, 
              overflow: 'auto',
              overflowX: 'hidden',
              pr: { xs: 0.25, sm: 0.5 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: hasPosts ? 'flex-start' : 'center',
              position: 'relative',
              '&::-webkit-scrollbar': { 
                width: { xs: '3px', sm: '4px' },
              },
              '&::-webkit-scrollbar-track': { 
                background: alpha(theme.palette.divider, 0.1),
                borderRadius: 2,
              },
              '&::-webkit-scrollbar-thumb': { 
                background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                borderRadius: 2,
                '&:hover': { 
                  background: `linear-gradient(180deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                }
              },
            }}
          >
            {/* Empty state for future dates */}
            {!hasPosts && (date.isAfter(dayjs(), 'day') || date.isSame(dayjs(), 'day')) && (
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  handleDateClick(date);
                }}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  py: 1,
                  cursor: 'pointer',
                  opacity: 0.6,
                  transition: 'all 0.3s',
                  '&:hover': {
                    opacity: 1,
                    transform: 'scale(1.1)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: isMobile ? 32 : 40,
                    height: isMobile ? 32 : 40,
                    borderRadius: '50%',
                    border: `2px dashed ${alpha(theme.palette.primary.main, 0.5)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    transition: 'all 0.3s',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      borderColor: theme.palette.primary.main,
                      borderStyle: 'solid',
                    },
                  }}
                >
                  <AddIcon 
                    sx={{ 
                      fontSize: isMobile ? '1.2rem' : '1.5rem',
                      color: theme.palette.primary.main,
                    }} 
                  />
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                    color: theme.palette.text.secondary,
                    fontWeight: 600,
                    textAlign: 'center',
                  }}
                >
                  {isMobile ? 'Add' : 'Schedule'}
                </Typography>
              </Box>
            )}

            {/* Posts list */}
            {hasPosts && (
              <Box sx={{ width: '100%' }}>
                {dayPosts.slice(0, isMobile ? 3 : 5).map((post, index) => {
                  const primaryPlatform = post.platforms?.[0] || 'Default';
                  const platformColor = PLATFORM_COLORS[primaryPlatform] || PLATFORM_COLORS.Default;
                  
                  return (
                    <Tooltip
                      key={index}
                      title={
                        <Box sx={{ p: 0.5 }}>
                          <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>
                            {dayjs(post.scheduleDateTime).format('hh:mm A')}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ opacity: 0.9 }}>
                            {post.platforms?.join(' • ')}
                          </Typography>
                          {post.postContent && (
                            <Typography variant="caption" display="block" mt={0.5} sx={{ opacity: 0.8, maxWidth: 200 }}>
                              {post.postContent.slice(0, 60)}...
                            </Typography>
                          )}
                        </Box>
                      }
                      arrow
                      placement="right"
                      enterDelay={300}
                    >
                      <Card
                        onClick={() => handlePostClick(post)}
                        sx={{
                          mb: { xs: 0.5, sm: 0.75 },
                          cursor: 'pointer',
                          borderRadius: { xs: 1.5, sm: 2 },
                          background: platformColor.gradient,
                          border: { xs: `1px solid ${alpha(platformColor.primary, 0.3)}`, sm: `2px solid ${alpha(platformColor.primary, 0.3)}` },
                          position: 'relative',
                          overflow: 'hidden',
                          boxShadow: isMobile ? 'none' : `0 2px 8px ${platformColor.glow}`,
                          transition: 'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                          '&:hover': {
                            transform: isMobile ? 'scale(1.02)' : 'translateX(4px) scale(1.03)',
                            boxShadow: `0 4px 16px ${platformColor.glow}`,
                            borderColor: alpha('#fff', 0.6),
                          },
                        }}
                      >
                        <CardContent sx={{ p: { xs: 0.75, sm: 1 }, '&:last-child': { pb: { xs: 0.75, sm: 1 } } }}>
                          <Stack spacing={0.5}>
                            {/* Compact time display */}
                            <Stack direction="row" spacing={{ xs: 0.5, sm: 0.75 }} alignItems="center" justifyContent="space-between">
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: { xs: 0.25, sm: 0.5 },
                                  px: { xs: 0.5, sm: 0.75 },
                                  py: 0.25,
                                  borderRadius: 1,
                                  bgcolor: alpha('#fff', 0.25),
                                  backdropFilter: 'blur(10px)',
                                  border: `1px solid ${alpha('#fff', 0.3)}`,
                                }}
                              >
                                <AccessTime sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, color: 'white' }} />
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                    fontWeight: 800,
                                    color: 'white',
                                    textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                  }}
                                >
                                  {dayjs(post.scheduleDateTime).format('HH:mm')}
                                </Typography>
                              </Box>
                              {post.mediaFileCount > 0 && (
                                <Chip
                                  icon={<ImageIcon sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, color: 'white !important' }} />}
                                  label={post.mediaFileCount}
                                  size="small"
                                  sx={{
                                    height: { xs: 18, sm: 20 },
                                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                    bgcolor: alpha('#000', 0.25),
                                    backdropFilter: 'blur(10px)',
                                    color: 'white',
                                    fontWeight: 700,
                                    border: `1px solid ${alpha('#fff', 0.2)}`,
                                    '& .MuiChip-icon': { marginLeft: '4px', marginRight: '-2px' },
                                  }}
                                />
                              )}
                            </Stack>
                            
                            {/* Platform Icons with Count */}
                            {post.accounts && post.accounts.length > 0 && (
                              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                                {(() => {
                                  // Count accounts per platform/provider
                                  const platformCounts = post.accounts.reduce((acc, account) => {
                                    const provider = account.provider || 'Unknown';
                                    acc[provider] = (acc[provider] || 0) + 1;
                                    return acc;
                                  }, {});
                                  
                                  return Object.entries(platformCounts).map(([platform, count]) => {
                                    const PlatformIcon = PLATFORM_ICONS[platform] || Event;
                                    const color = PLATFORM_COLORS[platform] || PLATFORM_COLORS.Default;
                                    return (
                                      <Tooltip title={`${platform} (${count} ${count === 1 ? 'account' : 'accounts'})`} key={platform}>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.25,
                                            px: 0.5,
                                            py: 0.25,
                                            borderRadius: 1,
                                            bgcolor: 'white',
                                            border: '1px solid rgba(255,255,255,0.3)',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                          }}
                                        >
                                          <PlatformIcon
                                            sx={{
                                              fontSize: { xs: 9, sm: 10 },
                                              color: color.primary,
                                            }}
                                          />
                                          <Typography
                                            sx={{
                                              fontSize: { xs: '0.55rem', sm: '0.6rem' },
                                              fontWeight: 700,
                                              color: color.primary,
                                              lineHeight: 1,
                                            }}
                                          >
                                            {count}
                                          </Typography>
                                        </Box>
                                      </Tooltip>
                                    );
                                  });
                                })()}
                              </Stack>
                            )}
                          </Stack>
                        </CardContent>
                      </Card>
                    </Tooltip>
                  );
                })}

                {/* Show more indicator if there are additional posts */}
                {dayPosts.length > (isMobile ? 3 : 5) && (
                  <Card
                    onClick={() => handleOpenDayPosts(date, dayPosts)}
                    sx={{
                      mt: 0.5,
                      cursor: 'pointer',
                      borderRadius: { xs: 1.5, sm: 2 },
                      background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.8)} 0%, ${alpha(theme.palette.secondary.dark, 0.9)} 100%)`,
                      border: `2px dashed ${alpha('#fff', 0.4)}`,
                      textAlign: 'center',
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        borderStyle: 'solid',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.4)}`,
                      },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 0.75, sm: 1 }, '&:last-child': { pb: { xs: 0.75, sm: 1 } } }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'white',
                          fontWeight: 800,
                          fontSize: { xs: '0.65rem', sm: '0.75rem' },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Event sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }} />
                        +{dayPosts.length - (isMobile ? 3 : 5)} more {isMobile ? '' : 'posts'}
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </Box>
            )}
          </Box>

          {/* Add Post Button - Floating action button for dates with posts */}
          {hasPosts && (date.isAfter(dayjs(), 'day') || date.isSame(dayjs(), 'day')) && (
            <Tooltip title="Add another post to this date" arrow placement="top">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleDateClick(date);
                }}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  position: 'absolute',
                  bottom: isMobile ? 4 : 8,
                  right: isMobile ? 4 : 8,
                  width: isMobile ? 28 : 36,
                  height: isMobile ? 28 : 36,
                  bgcolor: theme.palette.primary.main,
                  color: 'white',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
                  zIndex: 10,
                  transition: 'all 0.3s',
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark,
                    transform: 'scale(1.15) rotate(90deg)',
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.6)}`,
                  },
                }}
              >
                <AddIcon sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }} />
              </IconButton>
            </Tooltip>
          )}
        </Paper>
      </Zoom>
    );
  };

  const days = getDaysInMonth();

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 1, sm: 2, md: 3 }, px: { xs: 0.5, sm: 2, md: 3 } }}>
      {/* Header */}
      <Fade in timeout={600}>
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 1.5, sm: 2.5, md: 3 }, 
            mb: { xs: 1.5, sm: 2.5, md: 3 },
            borderRadius: { xs: 2, sm: 3, md: 4 },
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-20%',
              width: '400px',
              height: '400px',
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha('#fff', 0.1)} 0%, transparent 70%)`,
              animation: `${float} 6s ease-in-out infinite`,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '-30%',
              left: '-10%',
              width: '300px',
              height: '300px',
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha('#fff', 0.05)} 0%, transparent 70%)`,
              animation: `${float} 8s ease-in-out infinite`,
            },
          }}
        >
          <Stack 
            direction={isMobile ? 'column' : 'row'} 
            justifyContent="space-between" 
            alignItems={isMobile ? 'stretch' : 'center'} 
            spacing={2}
            sx={{ position: 'relative', zIndex: 1 }}
          >
            <Box sx={{ animation: `${slideUp} 0.6s ease-out` }}>
              <Stack direction="row" alignItems="center" spacing={2} mb={1}>
                <Box
                  sx={{
                    width: { xs: 40, sm: 48, md: 56 },
                    height: { xs: 40, sm: 48, md: 56 },
                    borderRadius: { xs: 2, sm: 2.5, md: 3 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: alpha('#fff', 0.2),
                    backdropFilter: 'blur(10px)',
                    boxShadow: `0 4px 16px ${alpha('#000', 0.1)}`,
                  }}
                >
                  <CalendarMonth sx={{ fontSize: { xs: 24, sm: 28, md: 32 }, color: 'white' }} />
                </Box>
                <Box>
                  <Typography 
                    variant={isMobile ? 'h6' : 'h4'} 
                    sx={{ 
                      color: 'white',
                      fontWeight: 800,
                      letterSpacing: '-0.5px',
                      textShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      fontSize: { xs: '1.1rem', sm: '1.5rem', md: '2.125rem' },
                    }}
                  >
                    Schedule Calendar
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: alpha('#fff', 0.9),
                      fontWeight: 500,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    }}
                  >
                    {isMobile ? 'Manage posts' : 'View and manage your scheduled posts'}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Month Navigation */}
            <Stack 
              direction="row" 
              spacing={{ xs: 0.75, sm: 1.5 }} 
              alignItems="center"
              sx={{ 
                animation: `${slideUp} 0.8s ease-out`,
              }}
            >
              <IconButton 
                onClick={handlePreviousMonth}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  bgcolor: alpha('#fff', 0.2),
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: alpha('#fff', 0.3),
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <ChevronLeft fontSize={isMobile ? 'small' : 'medium'} />
              </IconButton>
              
              {!isMobile && (
                <Button
                  variant="contained"
                  startIcon={<Today />}
                  onClick={handleToday}
                  sx={{
                    bgcolor: alpha('#fff', 0.2),
                    backdropFilter: 'blur(10px)',
                    color: 'white',
                    fontWeight: 600,
                    px: 3,
                    borderRadius: 2,
                    border: `2px solid ${alpha('#fff', 0.3)}`,
                    '&:hover': {
                      bgcolor: alpha('#fff', 0.3),
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${alpha('#000', 0.2)}`,
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  Today
                </Button>
              )}

              <Paper
                elevation={0}
                sx={{
                  px: { xs: 1.5, sm: 2.5, md: 3 },
                  py: { xs: 0.75, sm: 1, md: 1.5 },
                  bgcolor: alpha('#fff', 0.2),
                  backdropFilter: 'blur(10px)',
                  borderRadius: 2,
                  border: `2px solid ${alpha('#fff', 0.3)}`,
                }}
              >
                <Typography 
                  variant={isMobile ? 'body2' : 'h6'} 
                  sx={{ 
                    color: 'white',
                    fontWeight: 700,
                    minWidth: { xs: 90, sm: 120, md: 150 },
                    textAlign: 'center',
                    textShadow: '0 1px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  {isMobile ? currentMonth.format('MMM YY') : currentMonth.format('MMMM YYYY')}
                </Typography>
              </Paper>
              
              <IconButton 
                onClick={handleNextMonth}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  bgcolor: alpha('#fff', 0.2),
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: alpha('#fff', 0.3),
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <ChevronRight fontSize={isMobile ? 'small' : 'medium'} />
              </IconButton>
            </Stack>

            {/* Stats */}
            <Stack 
              direction="row" 
              spacing={{ xs: 1, sm: 1.5 }}
              sx={{
                animation: `${slideUp} 1s ease-out`,
                flexWrap: 'wrap',
                rowGap: { xs: 0.75, sm: 1 },
                columnGap: { xs: 0.75, sm: 1.25 },
                alignItems: 'center',
                justifyContent: { xs: 'center', sm: 'flex-end' },
                textAlign: 'center',
              }}
            >
              <Chip
                icon={<Event sx={{ color: 'white !important' }} />}
                label={isMobile ? `${postedCount}` : `${postedCount} Posted`}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  flexShrink: 0,
                  bgcolor: alpha('#3b82f6', 0.9),
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: { xs: '0.75rem', sm: '0.9rem' },
                  height: { xs: 32, sm: 40 },
                  px: { xs: 0.75, sm: 1 },
                  border: `2px solid ${alpha('#fff', 0.3)}`,
                  '& .MuiChip-icon': {
                    color: 'white',
                  },
                }}
              />
              <Chip
                icon={<Schedule sx={{ color: 'white !important' }} />}
                label={isMobile ? `${upcomingCount}` : `${upcomingCount} Upcoming`}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  flexShrink: 0,
                  bgcolor: alpha('#10b981', 0.9),
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: { xs: '0.75rem', sm: '0.9rem' },
                  height: { xs: 32, sm: 40 },
                  px: { xs: 0.75, sm: 1 },
                  border: `2px solid ${alpha('#fff', 0.3)}`,
                  '& .MuiChip-icon': {
                    color: 'white',
                  },
                }}
              />
              <Chip
                icon={<Event sx={{ color: 'white !important' }} />}
                label={isMobile ? `${totalCount}` : `${totalCount} Total`}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  flexShrink: 0,
                  bgcolor: alpha('#fff', 0.2),
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: { xs: '0.75rem', sm: '0.9rem' },
                  height: { xs: 32, sm: 40 },
                  px: { xs: 0.75, sm: 1 },
                  border: `2px solid ${alpha('#fff', 0.3)}`,
                  '& .MuiChip-icon': {
                    color: 'white',
                  },
                }}
              />
            </Stack>
          </Stack>
        </Paper>
      </Fade>

      {/* Loading State */}
      {loading && (
        <Fade in>
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" my={8}>
            <CircularProgress 
              size={60} 
              thickness={4}
              sx={{
                color: theme.palette.primary.main,
                mb: 2,
              }}
            />
            <Typography variant="h6" color="text.secondary" fontWeight={500}>
              Loading your schedule...
            </Typography>
          </Box>
        </Fade>
      )}

      {/* Error State */}
      {error && (
        <Fade in>
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 3,
              boxShadow: `0 4px 12px ${alpha('#f44336', 0.2)}`,
            }}
          >
            {error}
          </Alert>
        </Fade>
      )}

      {/* Calendar Grid */}
      {!loading && (
        <Fade in timeout={800}>
          <Paper 
            elevation={0}
            sx={{ 
              p: { xs: 0.5, sm: 2, md: 3 },
              borderRadius: { xs: 2, sm: 3, md: 4 },
              bgcolor: theme.palette.mode === 'dark' ? alpha('#fff', 0.02) : alpha('#000', 0.01),
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            {/* Day headers */}
            <Grid container spacing={{ xs: 0.25, sm: 0.5, md: 1.5 }} sx={{ mb: { xs: 0.5, sm: 1, md: 1.5 } }}>
              {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day) => (
                <Grid item xs={12/7} key={day}>
                  <Paper
                    elevation={0}
                    sx={{
                      py: { xs: 0.5, sm: 0.75, md: 1 },
                      borderRadius: { xs: 1, sm: 1.5, md: 2 },
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    }}
                  >
                    <Typography
                      variant={isMobile ? 'caption' : 'subtitle2'}
                      align="center"
                      fontWeight={700}
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        letterSpacing: { xs: '0px', sm: '0.5px' },
                        fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' },
                      }}
                    >
                      {isMobile ? day.charAt(0) : day}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Calendar days */}
            <Grid container spacing={{ xs: 0.25, sm: 0.5, md: 1.5 }}>
              {days.map((date, index) => {
                const dayPosts = date ? getPostsForDate(date) : [];
                return (
                  <Grid item xs={12/7} key={index}>
                    {renderDayCell(date, dayPosts)}
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Fade>
      )}

      {/* Post Preview Dialog */}
      <Dialog
        open={openPreview}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' }}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 4,
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-20%',
              width: '300px',
              height: '300px',
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha('#fff', 0.1)} 0%, transparent 70%)`,
            },
          }}
        >
          <Stack spacing={1.5} sx={{ position: 'relative', zIndex: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha('#fff', 0.2),
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <Visibility sx={{ color: 'white' }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 0.25 }}>
                      Post Details
                    </Typography>
                    {/* Post ID Display */}
                    {selectedPost && selectedPost.postId && (
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ opacity: 0.9 }}>
                        <Event sx={{ fontSize: '0.875rem', color: 'white' }} />
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'white',
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            letterSpacing: '0.3px',
                          }}
                        >
                          Post ID: {selectedPost.postId}
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Box>
              <Chip
                icon={<Schedule sx={{ color: 'white !important' }} />}
                label={selectedPost && dayjs(selectedPost.scheduleDateTime).format('MMM DD, YYYY - HH:mm A')}
                sx={{
                  bgcolor: alpha('#fff', 0.2),
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  fontWeight: 600,
                  border: `1px solid ${alpha('#fff', 0.3)}`,
                }}
              />
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {selectedPost && (
            <Box>
              {/* Platforms */}
              <Box mb={3}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                  <Box
                    sx={{
                      width: 8,
                      height: 24,
                      borderRadius: 1,
                      background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    }}
                  />
                  <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                    Publishing Platforms
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  {selectedPost.platforms?.map((platform, idx) => {
                    const Icon = PLATFORM_ICONS[platform] || Event;
                    const color = PLATFORM_COLORS[platform] || PLATFORM_COLORS.Default;
                    return (
                      <Chip
                        key={idx}
                        icon={<Icon sx={{ color: 'white !important' }} />}
                        label={platform}
                        sx={{
                          background: color.gradient,
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          height: 36,
                          px: 1,
                          boxShadow: `0 4px 12px ${color.glow}`,
                          border: `2px solid ${alpha(color.primary, 0.3)}`,
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 6px 16px ${color.glow}`,
                          },
                          transition: 'all 0.2s',
                        }}
                      />
                    );
                  })}
                </Stack>
              </Box>

              {/* Accounts */}
              <Box mb={3}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                  <Box
                    sx={{
                      width: 8,
                      height: 24,
                      borderRadius: 1,
                      background: `linear-gradient(180deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                    }}
                  />
                  <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                    Connected Accounts
                  </Typography>
                </Stack>
                <Grid container spacing={1.5}>
                  {selectedPost.accounts?.map((account, idx) => {
                    const color = PLATFORM_COLORS[account.provider] || PLATFORM_COLORS.Default;
                    return (
                      <Grid item xs={12} sm={6} key={idx}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            background: color.light,
                            border: `2px solid ${alpha(color.primary, 0.2)}`,
                            transition: 'all 0.2s',
                            '&:hover': {
                              transform: 'translateX(4px)',
                              boxShadow: `0 4px 12px ${color.glow}`,
                            },
                          }}
                        >
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                background: color.gradient,
                                fontWeight: 700,
                                boxShadow: `0 4px 8px ${color.glow}`,
                              }}
                            >
                              {account.accountName[0]}
                            </Avatar>
                            <Box flex={1}>
                              <Typography variant="body2" fontWeight={700} color="text.primary">
                                {account.accountName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {account.provider}
                              </Typography>
                            </Box>
                            <Chip
                              label={account.status}
                              size="small"
                              sx={{
                                bgcolor: account.status === 'Active' ? '#dcfce7' : '#fee2e2',
                                color: account.status === 'Active' ? '#15803d' : '#dc2626',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                              }}
                            />
                          </Stack>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>

              {/* Content */}
              <Box mb={3}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                  <Box
                    sx={{
                      width: 8,
                      height: 24,
                      borderRadius: 1,
                      background: `linear-gradient(180deg, #f59e0b 0%, #d97706 100%)`,
                    }}
                  />
                  <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                    Post Content
                  </Typography>
                </Stack>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 2.5, 
                    borderRadius: 3,
                    bgcolor: theme.palette.mode === 'dark' ? alpha('#fff', 0.02) : alpha('#000', 0.02),
                    border: `2px solid ${theme.palette.divider}`,
                    maxHeight: '300px',
                    overflow: 'auto',
                    '&::-webkit-scrollbar': { width: '8px' },
                    '&::-webkit-scrollbar-track': { background: alpha(theme.palette.divider, 0.1) },
                    '&::-webkit-scrollbar-thumb': { 
                      background: alpha(theme.palette.primary.main, 0.3),
                      borderRadius: '4px',
                      '&:hover': { background: alpha(theme.palette.primary.main, 0.5) }
                    },
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                    {selectedPost.postContent}
                  </Typography>
                </Paper>
              </Box>

              {/* Media */}
              {selectedPost.mediaFiles && selectedPost.mediaFiles.length > 0 && (
                <Box mb={3}>
                  <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                    <Box
                      sx={{
                        width: 8,
                        height: 24,
                        borderRadius: 1,
                        background: `linear-gradient(180deg, #8b5cf6 0%, #7c3aed 100%)`,
                      }}
                    />
                    <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                      Media Files ({selectedPost.mediaFileCount})
                    </Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    {selectedPost.mediaFiles.map((media, idx) => (
                      <Grid item xs={6} sm={4} key={idx}>
                        <Zoom in timeout={300 * (idx + 1)}>
                          <Paper 
                            elevation={0}
                            sx={{ 
                              aspectRatio: '1/1',
                              position: 'relative',
                              overflow: 'hidden',
                              borderRadius: 3,
                              border: `3px solid ${theme.palette.divider}`,
                              transition: 'all 0.3s',
                              '&:hover': {
                                transform: 'scale(1.05)',
                                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                                borderColor: theme.palette.primary.main,
                              },
                            }}
                          >
                            <img
                              src={media.s3Url}
                              alt={media.originalName}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                bgcolor: alpha('#000', 0.7),
                                backdropFilter: 'blur(10px)',
                                p: 1,
                                transform: 'translateY(100%)',
                                transition: 'transform 0.3s',
                                '.MuiPaper-root:hover &': {
                                  transform: 'translateY(0)',
                                },
                              }}
                            >
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: 'white',
                                  fontWeight: 500,
                                  display: 'block',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {media.originalName}
                              </Typography>
                            </Box>
                          </Paper>
                        </Zoom>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Metadata */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  border: `2px dashed ${alpha(theme.palette.info.main, 0.3)}`,
                }}
              >
                <Stack spacing={0.5}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Event sx={{ fontSize: '1rem', color: theme.palette.info.main }} />
                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                      Content Type:
                    </Typography>
                    <Chip label={selectedPost.contentType} size="small" color="info" />
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Schedule sx={{ fontSize: '1rem', color: theme.palette.success.main }} />
                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                      Created:
                    </Typography>
                    <Typography variant="caption" color="text.primary">
                      {dayjs(selectedPost.CreatedAt).format('MMM DD, YYYY HH:mm A')}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Schedule sx={{ fontSize: '1rem', color: theme.palette.warning.main }} />
                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                      Updated:
                    </Typography>
                    <Typography variant="caption" color="text.primary">
                      {dayjs(selectedPost.UpdatedAt).format('MMM DD, YYYY HH:mm A')}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          {cancelError && (
            <Alert severity="error" sx={{ flex: 1, mr: 2 }}>
              {cancelError}
            </Alert>
          )}
          {cancelSuccess && (
            <Alert severity="success" sx={{ flex: 1, mr: 2 }}>
              Post cancelled successfully!
            </Alert>
          )}
          {selectedPost && dayjs(selectedPost.scheduleDateTime).isAfter(dayjs()) && !cancelSuccess && (
            <Button
              onClick={handleCancelPost}
              disabled={cancellingPost}
              variant="outlined"
              color="error"
              startIcon={cancellingPost ? <CircularProgress size={16} /> : <Delete />}
              sx={{
                fontWeight: 600,
                px: 3,
                py: 1,
                borderRadius: 2,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`,
                },
                transition: 'all 0.2s',
              }}
            >
              {cancellingPost ? 'Cancelling...' : 'Cancel Post'}
            </Button>
          )}
          <Button 
            onClick={handleClosePreview}
            variant="contained"
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              fontWeight: 600,
              px: 4,
              py: 1,
              borderRadius: 2,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
              transition: 'all 0.2s',
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Day Posts Dialog */}
      <Dialog
        open={openDayPosts}
        onClose={handleCloseDayPosts}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' }}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 4,
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
            color: 'white',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Scheduled Posts
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {selectedDayDate ? selectedDayDate.format('MMM DD, YYYY') : ''}
              </Typography>
            </Box>
            <Chip
              icon={<Event sx={{ color: 'white !important' }} />}
              label={`${selectedDayPosts.length} posts`}
              sx={{
                bgcolor: alpha('#fff', 0.2),
                color: 'white',
                fontWeight: 700,
              }}
            />
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            {selectedDayPosts.map((post, idx) => {
              const primaryPlatform = post.platforms?.[0] || 'Default';
              const color = PLATFORM_COLORS[primaryPlatform] || PLATFORM_COLORS.Default;
              return (
                <Card
                  key={idx}
                  onClick={() => {
                    handlePostClick(post);
                    handleCloseDayPosts();
                  }}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 2,
                    background: color.gradient,
                    border: `2px solid ${alpha(color.primary, 0.3)}`,
                    boxShadow: `0 4px 12px ${color.glow}`,
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateX(4px)',
                      boxShadow: `0 6px 16px ${color.glow}`,
                      borderColor: alpha('#fff', 0.6),
                    },
                  }}
                >
                  <CardContent sx={{ p: 1.5 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Stack spacing={0.5}>
                        <Typography
                          variant="caption"
                          sx={{ color: 'white', fontWeight: 800 }}
                        >
                          {dayjs(post.scheduleDateTime).format('hh:mm A')}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: alpha('#fff', 0.9) }}
                        >
                          {post.platforms?.join(' • ')}
                        </Typography>
                      </Stack>
                      <Chip
                        size="small"
                        label={post.contentType || 'Post'}
                        sx={{
                          bgcolor: alpha('#fff', 0.2),
                          color: 'white',
                          fontWeight: 700,
                        }}
                      />
                    </Stack>
                    {post.postContent && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          color: alpha('#fff', 0.95),
                          mt: 0.75,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {post.postContent}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseDayPosts}
            variant="contained"
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
              color: 'white',
              fontWeight: 600,
              px: 4,
              py: 1,
              borderRadius: 2,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.4)}`,
              },
              transition: 'all 0.2s',
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog
        open={openSchedule}
        onClose={handleCloseSchedule}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' }}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 4,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            pb: 3,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-20%',
              width: '300px',
              height: '300px',
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha('#fff', 0.15)} 0%, transparent 70%)`,
            },
          }}
        >
          <Stack spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha('#fff', 0.2),
                  backdropFilter: 'blur(10px)',
                  border: `2px solid ${alpha('#fff', 0.3)}`,
                }}
              >
                <CalendarMonth sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box flex={1}>
                <Typography variant="h6" fontWeight={700} mb={0.5}>
                  Schedule a Post
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.95 }}>
                  Select date and time for your post
                </Typography>
              </Box>
            </Stack>

            {/* Selected Date Display */}
            {selectedScheduleDate && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  background: alpha('#fff', 0.15),
                  backdropFilter: 'blur(10px)',
                  border: `2px solid ${alpha('#fff', 0.3)}`,
                  borderRadius: 3,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'white',
                      boxShadow: `0 4px 12px ${alpha('#000', 0.2)}`,
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                      {selectedScheduleDate.format('MMM').toUpperCase()}
                    </Typography>
                    <Typography variant="h5" fontWeight={800} color="primary">
                      {selectedScheduleDate.format('D')}
                    </Typography>
                  </Box>
                  <Box flex={1}>
                    <Typography variant="h6" fontWeight={700} color="white">
                      {selectedScheduleDate.format('dddd')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: alpha('#fff', 0.9) }}>
                      {selectedScheduleDate.format('MMMM DD, YYYY')}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            )}
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 3, pt: 3 }}>
          {/* Time Selection */}
          <Box mb={3}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Box
                sx={{
                  width: 6,
                  height: 24,
                  borderRadius: 1,
                  background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                }}
              />
              <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                Select Time
              </Typography>
            </Stack>

            {/* Time Dropdown */}
            <Paper
              elevation={0}
              sx={{
                p: 0.5,
                mb: 2,
                maxHeight: 200,
                overflow: 'auto',
                border: `2px solid ${theme.palette.divider}`,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.paper, 0.5),
                '&::-webkit-scrollbar': { width: '8px' },
                '&::-webkit-scrollbar-track': { background: alpha(theme.palette.divider, 0.1) },
                '&::-webkit-scrollbar-thumb': {
                  background: alpha(theme.palette.primary.main, 0.3),
                  borderRadius: '4px',
                  '&:hover': { background: alpha(theme.palette.primary.main, 0.5) }
                },
              }}
            >
              {timeOptions.map((time) => (
                <Box
                  key={time}
                  onClick={() => handleTimeSelect(time)}
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 0.5,
                    bgcolor: selectedTime === time
                      ? alpha(theme.palette.primary.main, 0.15)
                      : 'transparent',
                    border: '2px solid',
                    borderColor: selectedTime === time
                      ? theme.palette.primary.main
                      : 'transparent',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  <AccessTime
                    sx={{
                      color: selectedTime === time ? theme.palette.primary.main : theme.palette.text.secondary,
                      fontSize: 20,
                    }}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={selectedTime === time ? 700 : 500}
                    color={selectedTime === time ? 'primary' : 'text.primary'}
                  >
                    {time}
                  </Typography>
                  {selectedTime === time && (
                    <Box
                      sx={{
                        ml: 'auto',
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: theme.palette.primary.main,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: 'white', fontSize: '1.2rem', lineHeight: 1 }}>
                        ✓
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Paper>
          </Box>

          {/* Quick Pick */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Box
                sx={{
                  width: 6,
                  height: 24,
                  borderRadius: 1,
                  background: `linear-gradient(180deg, #f59e0b 0%, #d97706 100%)`,
                }}
              />
              <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                Quick Pick
              </Typography>
            </Stack>

            <Grid container spacing={1.5}>
              {quickPickTimes.map((time) => (
                <Grid item xs={4} sm={3} key={time}>
                  <Button
                    fullWidth
                    variant={selectedTime === time ? 'contained' : 'outlined'}
                    onClick={() => handleTimeSelect(time)}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      border: '2px solid',
                      borderColor: selectedTime === time
                        ? theme.palette.primary.main
                        : alpha(theme.palette.primary.main, 0.3),
                      bgcolor: selectedTime === time
                        ? theme.palette.primary.main
                        : 'transparent',
                      color: selectedTime === time ? 'white' : theme.palette.primary.main,
                      boxShadow: selectedTime === time
                        ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                        : 'none',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        bgcolor: selectedTime === time
                          ? theme.palette.primary.dark
                          : alpha(theme.palette.primary.main, 0.08),
                        transform: 'translateY(-2px)',
                        boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.25)}`,
                      },
                      transition: 'all 0.2s',
                    }}
                  >
                    {time}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Info Alert */}
          <Alert
            severity="info"
            icon={<Schedule />}
            sx={{
              mt: 3,
              borderRadius: 2,
              border: `2px solid ${alpha(theme.palette.info.main, 0.3)}`,
            }}
          >
            <Typography variant="body2" fontWeight={500}>
              You'll be redirected to create your post content after confirming the schedule time.
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            gap: 1.5,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <Button
            onClick={handleCloseSchedule}
            variant="outlined"
            fullWidth={isMobile}
            sx={{
              color: theme.palette.text.secondary,
              borderColor: theme.palette.divider,
              borderWidth: 2,
              fontWeight: 600,
              py: 1.25,
              px: 3,
              borderRadius: 2,
              '&:hover': {
                borderColor: theme.palette.text.secondary,
                bgcolor: alpha(theme.palette.text.secondary, 0.05),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSchedulePost}
            variant="contained"
            fullWidth={isMobile}
            startIcon={<Event />}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              fontWeight: 700,
              py: 1.25,
              px: 4,
              borderRadius: 2,
              boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                transform: 'translateY(-2px)',
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
              },
              transition: 'all 0.2s',
            }}
          >
            Continue to Create Post
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CalendarSchedule;